import { roundToDecimal } from "../utils/math";

/**
 * Emergency Fund target calculator
 */
export function emergencyFund(
  monthlyExpenses: number,
  targetMonths = 6
): { targetAmount: number; threeMonthBuffer: number; sixMonthBuffer: number } {
  return {
    targetAmount: roundToDecimal(monthlyExpenses * targetMonths, 2),
    threeMonthBuffer: roundToDecimal(monthlyExpenses * 3, 2),
    sixMonthBuffer: roundToDecimal(monthlyExpenses * 6, 2),
  };
}

/**
 * Calculates monthly savings needed to reach a future financial goal
 */
export function savingsGoal(
  targetAmount: number,
  currentSavings: number,
  annualRate: number,
  targetMonths: number
): { monthlyDepositNeeded: number; totalPrincipal: number; totalInterestEarned: number } {
  if (targetMonths <= 0) {
    return {
      monthlyDepositNeeded: 0,
      totalPrincipal: currentSavings,
      totalInterestEarned: 0,
    };
  }

  const r = annualRate / 100 / 12;
  const n = targetMonths;
  const pv = currentSavings;
  const fv = targetAmount;

  let monthlyDepositNeeded = 0;
  if (r > 0) {
    // FV of current savings = pv * (1 + r)^n
    const fvCurrent = pv * Math.pow(1 + r, n);
    const shortfall = Math.max(0, fv - fvCurrent);
    
    // PMT to accumulate the shortfall
    // PMT = Shortfall * r / ((1 + r)^n - 1)
    const divisor = Math.pow(1 + r, n) - 1;
    monthlyDepositNeeded = divisor > 0 ? (shortfall * r) / divisor : 0;
  } else {
    const shortfall = Math.max(0, fv - pv);
    monthlyDepositNeeded = shortfall / n;
  }

  const totalPrincipal = pv + monthlyDepositNeeded * n;
  const totalInterestEarned = Math.max(0, fv - totalPrincipal);

  return {
    monthlyDepositNeeded: roundToDecimal(monthlyDepositNeeded, 2),
    totalPrincipal: roundToDecimal(totalPrincipal, 2),
    totalInterestEarned: roundToDecimal(totalInterestEarned, 2),
  };
}

/**
 * Calculates inflation impact on purchasing power
 */
export function inflationImpact(
  currentAmount: number,
  annualInflation: number,
  years: number
): { futurePurchasingPower: number; lossInValue: number } {
  const inflationDec = Math.max(-0.99, annualInflation / 100);
  
  // Future purchasing power = currentAmount / (1 + inflation)^years
  const futurePurchasingPower = currentAmount / Math.pow(1 + inflationDec, years);
  const lossInValue = currentAmount - futurePurchasingPower;

  return {
    futurePurchasingPower: roundToDecimal(futurePurchasingPower, 2),
    lossInValue: roundToDecimal(lossInValue, 2),
  };
}

/**
 * Real Rate of Return (Fisher Equation)
 * Formula: Real Rate = (1 + Nominal) / (1 + Inflation) - 1
 */
export function realReturn(nominalRate: number, annualInflation: number): number {
  const n = nominalRate / 100;
  const inf = Math.max(-0.99, annualInflation / 100);
  const real = (1 + n) / (1 + inf) - 1;
  
  return roundToDecimal(real * 100, 2); // as percentage
}

/**
 * Fixed Deposit / CD Maturity Calculator
 */
export function fixedDeposit(
  principal: number,
  interestRate: number,
  termMonths: number,
  compoundingFrequency = 4 // default quarterly compounding
): { maturityValue: number; interestEarned: number } {
  const r = interestRate / 100;
  const t = termMonths / 12;
  const n = compoundingFrequency > 0 ? compoundingFrequency : 1;

  // Formula: A = P * (1 + r/n)^(n*t)
  const maturityValue = principal * Math.pow(1 + r / n, n * t);
  const interestEarned = maturityValue - principal;

  return {
    maturityValue: roundToDecimal(maturityValue, 2),
    interestEarned: roundToDecimal(interestEarned, 2),
  };
}

/**
 * Premium Bonds Expected Return Calculator (UK, NS&I, July 2026 rate)
 */
export function calculatePremiumBondsExpectedReturn(
  investmentAmount: number,
  months: number,
  prizeRate = 3.8,
  inflationRate = 2.5,
  oddsPerBond = 22000
): {
  expectedReturn: number;
  expectedReturnRate: number;
  oddsOfWinningNothing: number;
  medianReturn: number;
  medianReturnRate: number;
  inflationAdjustedReturn: number;
  expectedAnnualPrizes: number;
} {
  const validInvestment = Math.max(25, Math.min(50000, Math.abs(investmentAmount)));
  const validMonths = Math.max(1, Math.round(Math.abs(months)));
  const years = validMonths / 12;
  const prizeRateDecimal = Math.max(0, prizeRate) / 100;
  const inflationRateDecimal = Math.max(0, inflationRate) / 100;
  const odds = Math.max(1, oddsPerBond);

  // Expected return (mean)
  const expectedReturn = validInvestment * prizeRateDecimal * years;
  const expectedReturnRate = prizeRate;

  // Probability of winning nothing over the full period
  const monthlySurvivalProb = 1 - 1 / odds;
  const totalBondMonths = validInvestment * validMonths;
  const oddsOfWinningNothing = Math.pow(monthlySurvivalProb, totalBondMonths);

  // Expected number of prizes per year
  const expectedAnnualPrizes = validInvestment * 12 / odds;

  // Median return approximation
  let medianReturn: number;
  let medianReturnRate: number;
  if (expectedAnnualPrizes < 1) {
    medianReturn = 0;
    medianReturnRate = 0;
  } else {
    const medianFactor = 1 - 0.5 / Math.sqrt(expectedAnnualPrizes + 1);
    const annualMedianReturn = validInvestment * prizeRateDecimal * medianFactor;
    medianReturn = annualMedianReturn * years;
    medianReturnRate = prizeRate * medianFactor;
  }

  // Inflation-adjusted return (real return using Fisher equation)
  const nominalReturn = expectedReturn;
  const realRate = (1 + prizeRateDecimal) / (1 + inflationRateDecimal) - 1;
  const inflationAdjustedReturn = validInvestment * realRate * years;

  return {
    expectedReturn: Math.round(expectedReturn * 100) / 100,
    expectedReturnRate: Math.round(expectedReturnRate * 100) / 100,
    oddsOfWinningNothing: Math.round(oddsOfWinningNothing * 10000) / 10000,
    medianReturn: Math.round(medianReturn * 100) / 100,
    medianReturnRate: Math.round(medianReturnRate * 100) / 100,
    inflationAdjustedReturn: Math.round(inflationAdjustedReturn * 100) / 100,
    expectedAnnualPrizes: Math.round(expectedAnnualPrizes * 100) / 100,
  };
}

/**
 * Singapore Savings Bonds (SSB) Step-Up Interest Calculator (SBAUG26 / GX26080T issue)
 */
export function calculateSingaporeSavingsBond(
  principal: number,
  yearsToHold = 10
): {
  totalInterest: number;
  averageYield: number;
  endBalance: number;
  modeledPrincipal: number;
  denominationNotice: string;
  yearlyBreakdown: Array<{
    year: number;
    rate: number;
    contributions: number;
    interest: number;
    yearInterest: number;
    balance: number;
  }>;
  isSsb?: boolean;
} {
  // SBAUG26 (GX26080T) step-up interest rates by year (1 to 10)
  const ssbRates = [1.46, 1.75, 1.80, 1.80, 1.90, 2.11, 2.27, 2.43, 2.58, 2.72];
  const safePrincipal = Number.isFinite(principal) ? Math.max(0, Math.min(200_000, principal)) : 0;
  const modeledPrincipal = Math.floor(safePrincipal / 500) * 500;
  const holdYears = Number.isFinite(yearsToHold) ? Math.min(10, Math.max(1, Math.floor(yearsToHold))) : 1;

  let totalInterest = 0;
  const yearlyBreakdown = [];

  for (let i = 0; i < holdYears; i++) {
    const yearRate = ssbRates[i];
    const yearInterest = modeledPrincipal * (yearRate / 100);
    totalInterest += yearInterest;

    yearlyBreakdown.push({
      year: i + 1,
      rate: yearRate,
      contributions: modeledPrincipal,
      interest: roundToDecimal(totalInterest, 2),
      yearInterest: roundToDecimal(yearInterest, 2),
      balance: roundToDecimal(modeledPrincipal + totalInterest, 2),
    });
  }

  // Arithmetic coupon average is distinct from MAS's effective annual return
  // (2.06% over ten years for this issue), which incorporates coupon timing.
  const averageYield = modeledPrincipal > 0 ? (totalInterest / modeledPrincipal / holdYears) * 100 : 0;
  const denominationNotice = modeledPrincipal !== principal
    ? modeledPrincipal === 0
      ? "Enter at least S$500. No bond is modelled until one complete S$500 block is available."
      : `SSBs use S$500 blocks. The calculation uses S$${modeledPrincipal.toLocaleString()} of the amount entered; check your remaining S$200,000 individual holding headroom.`
    : "This models complete S$500 blocks only. Check your remaining S$200,000 individual holding headroom.";

  return {
    totalInterest: roundToDecimal(totalInterest, 2),
    averageYield: roundToDecimal(averageYield, 2),
    endBalance: roundToDecimal(modeledPrincipal + totalInterest, 2),
    modeledPrincipal,
    denominationNotice,
    yearlyBreakdown,
    isSsb: true,
  };
}

/**
 * Capital One 360 Savings settlement estimator (US, July 2026).
 *
 * Documented anchors, all from the court-approved settlement website
 * (capitalone360savingsaccountlitigation.com, site updated 9 July 2026)
 * unless noted:
 * - $425,000,000 Settlement Fund; Class Cash Payments are pro-rata on each
 *   member's "Individual Recognized Claim", the approximate additional
 *   interest the 360 Savings account would have earned at the 360
 *   Performance Savings rate during the class period (18 Sep 2019 to
 *   16 Jun 2025, 69 months).
 * - Deductions before distribution: attorneys' fees requested at no greater
 *   than 15% of the fund, roughly $1.8M in expenses, and $10,000 x 26
 *   service awards.
 * - The court-appointed Special Master put reasonable class damages at
 *   roughly $742M to $1.098B; the two payout scenarios below divide the net
 *   fund by those endpoints.
 * - APY-gap anchors: 1.90% vs 1.00% at the September 2019 launch (NY AG
 *   complaint, May 2025); 0.40% vs 0.30% through the pandemic plateau (CFPB
 *   complaint, January 2025); 4.35% vs 0.30% at the peak (Sim v. Capital
 *   One complaint).
 * - Prospective relief: 360 Savings holders get the 360 Performance Savings
 *   APY going forward (3.00% as of 27 July 2026, capitalone.com) vs the
 *   historical 0.30% legacy rate.
 */
const CO360_SETTLEMENT_FUND = 425_000_000;
const CO360_FEES_MAX = 0.15; // attorneys' fees requested: no greater than 15%
const CO360_EXPENSES = 1_800_000;
const CO360_SERVICE_AWARDS = 260_000; // $10,000 x 26 class representatives
const CO360_DAMAGES_LOW = 742_000_000; // Special Master lower bound
const CO360_DAMAGES_HIGH = 1_098_000_000; // Special Master upper bound
const CO360_PERF_APY_JULY_2026 = 3.0; // capitalone.com, effective 27 July 2026
const CO360_LEGACY_RATE = 0.3; // 360 Savings historical rate floor

export function calculateCapitalOne360SettlementEstimate(
  avgBalance: number,
  monthsHeld: number,
  avgRateGap: number,
  stillHasAccount: string
): {
  recognizedClaimEstimate: number;
  estimatedPayoutLow: number;
  estimatedPayoutHigh: number;
  annualFutureBenefit: number;
} {
  const balance = Math.max(0, avgBalance || 0);
  const months = Math.min(69, Math.max(0, monthsHeld || 0));
  const gap = Math.max(0, avgRateGap || 0) / 100;

  // Individual Recognized Claim: extra interest at the Performance rate.
  const recognizedClaim = balance * gap * (months / 12);

  // Net fund after the documented deductions: about $359.19M.
  const netFund =
    CO360_SETTLEMENT_FUND * (1 - CO360_FEES_MAX) -
    CO360_EXPENSES -
    CO360_SERVICE_AWARDS;

  // Pro-rata scenarios: if aggregate recognized claims land at the Special
  // Master's upper bound, each dollar of claim pays ~$0.33; at the lower
  // bound, ~$0.48. Actual results depend on the final class-wide total.
  const estimatedPayoutLow = recognizedClaim * (netFund / CO360_DAMAGES_HIGH);
  const estimatedPayoutHigh = recognizedClaim * (netFund / CO360_DAMAGES_LOW);

  // Prospective rate matching for accounts still open, at July 2026 rates.
  const annualFutureBenefit =
    stillHasAccount === "yes"
      ? balance * ((CO360_PERF_APY_JULY_2026 - CO360_LEGACY_RATE) / 100)
      : 0;

  return {
    recognizedClaimEstimate: Math.round(recognizedClaim),
    estimatedPayoutLow: Math.round(estimatedPayoutLow),
    estimatedPayoutHigh: Math.round(estimatedPayoutHigh),
    annualFutureBenefit: Math.round(annualFutureBenefit),
  };
}
